import { useCallback, useEffect, useState } from 'react'

import { rejectAvailable, acceptAvailable } from 'app/dataProviders/encashments/status'
import { EncashmentsRowItemType, EncashmentsStatusType } from 'app/dataTypes/reports/data'
import { EncashmentsAction } from 'app/dataTypes/reports/data/finances/encashments/action'

const createSetByStatus = (
  data: Array<EncashmentsRowItemType>,
  statusAvailable: (status: EncashmentsStatusType | null) => boolean,
) => {
  const res = data
    .map(item =>
      statusAvailable(item.encashmentsSummary.status) ? item.encashmentsSummary.salePointId : null
    )
  const array = res.filter((v: any): v is string => typeof v === 'string')
  return new Set(array)
}

export function useSelection(data: Array<EncashmentsRowItemType>) {
  const [selectionMode, setSelectionMode] = useState<null | EncashmentsAction>(null)

  const [selection, setSelection] = useState<Set<string>>(new Set())
  const [allSelectedNumber, setAllSelectedNumber] = useState(0)

  const changeSelection = useCallback((salePointId: string, checked: boolean) => {
    setSelection((prevState) => {
      const nextState = new Set(prevState)
      if (checked) {
        nextState.add(salePointId)
      } else {
        nextState.delete(salePointId)
      }
      return nextState
    })
  }, [])

  const reset = useCallback(() => {
    setSelectionMode(null)
  }, [])

  const selectAll = useCallback((mode: EncashmentsAction) => {
    const selectionAll = createSetByStatus(
      data,
      mode === EncashmentsAction.Accept ? acceptAvailable : rejectAvailable,
    )
    setSelection(selectionAll)
    setAllSelectedNumber(selectionAll.size)
  }, [data])

  const selectNothing = useCallback(() => {
    setSelection(new Set())
    setAllSelectedNumber(0)
  }, [])

  const changeSelectionAll = useCallback((selectedAll: boolean) => {
    if (selectedAll && selectionMode) {
      selectAll(selectionMode)
      return
    }
    selectNothing()
  }, [selectionMode, selectAll, selectNothing])

  useEffect(() => {
    if (!selectionMode) {
      selectNothing()
      return
    }
    selectAll(selectionMode)
  }, [selectionMode]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    selection,
    selectionMode,
    setSelectionMode,
    changeSelection,
    reset,
    selectedAll: !!allSelectedNumber && allSelectedNumber === selection.size,
    changeSelectionAll,
  }
}
